import { useRouter } from 'hooks/useRouter'
import { useEffect, useRef } from 'react'
import { useAuth } from 'state/auth/hooks'
import { useToggleDisplayNotification } from 'state/notification/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button'
import TransText from 'UIKit/Text/TransText'
import { handleClickElement } from 'utils/clickElement'
import Column from '../Column'
import { RowBetween } from '../Row'
import NotificationContent from './Notificontent'

const NotificationLayout: React.FC<BoxProps> = ({ ...props }) => {
  const { isOpened: isOpenNotification, toggle: updateDisplayNotification } = useToggleDisplayNotification()
  const container = useRef(null)
  const { isSigned } = useAuth()
  const router = useRouter()
  const cleanupRef = useRef<() => void>(null)

  useEffect(() => {
    if (isOpenNotification) {
      cleanupRef.current = handleClickElement({
        elementId: 'app-content',
        callback: () => updateDisplayNotification(false),
      })
    }

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [isOpenNotification])

  useEffect(() => {
    if (!isSigned) updateDisplayNotification(false)
  }, [isSigned])

  useEffect(() => {
    const eventHandler = () => {
      updateDisplayNotification(false)
    }

    router.events.on('routeChangeComplete', eventHandler)
    return () => {
      router.events.off('routeChangeComplete', eventHandler)
    }
  }, [router.events])

  return (
    <StyledNotificationContainer p="20px 0px 0px 0px" ref={container} $isOpen={isOpenNotification} {...props}>
      <Column width="100%" height="100%">
        <RowBetween width="100%" px="16px" maxHeight="24px">
          <TransText fontSize="14px" fontWeight={700} color="text_primary">
            Notification
          </TransText>
          <StyledButton variant="text" onClick={() => updateDisplayNotification(false)} height={24} pr="0px !important">
            <Icons.CloseIcon width="20px" fill={theme.colors.textTertiary} />
          </StyledButton>
        </RowBetween>

        {isOpenNotification && <NotificationContent style={{ flex: 1 }} />}
      </Column>
    </StyledNotificationContainer>
  )
}

const StyledNotificationContainer = styled(Box)<{ $isOpen: boolean }>`
  will-change: animation;
  backdrop-filter: blur(15px);
  background: ${({ theme }) => theme.colors.bg_surface};
  width: 100%;
  height: 100%;
  max-height: 100%;
  position: fixed;
  top: 0;
  right: ${({ $isOpen }) => ($isOpen ? '0%' : '-100%')};
  cursor: pointer;
  transition: ${({ theme: { transitions } }) => transitions.medium};

  ${({ theme: { mediaQueries } }) => mediaQueries.md} {
    height: ${({ theme: { topbarHeight } }) => `calc(100vh - ${topbarHeight})`};
    max-width: 370px;
    width: 100%;
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    position: absolute;
    top: ${({ theme: { topbarHeight } }) => topbarHeight};
    overflow: hidden;
  }
`

const StyledButton = styled(Button)`
  z-index: ${({ theme }) => theme.zIndices.modal};
`

export default NotificationLayout
